import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "@helpers/translate";
import { COMMUNITY_TIME_RANGE_OPTIONS } from "./communityService";
import "./CommunityDateRange.sass";

const MENU_HEIGHT = 150;

export const CommunityDateRange = ({ value, onChange, disabled = false }) => {
  const { __ } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const rootRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);

  const selectedOption =
    COMMUNITY_TIME_RANGE_OPTIONS.find((option) => option.id === value) ||
    COMMUNITY_TIME_RANGE_OPTIONS[0];

  useEffect(() => {
    if (!isMobile) {
      setIsOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const resolveOpenDirection = () => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return false;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    return spaceBelow < MENU_HEIGHT && spaceAbove > spaceBelow;
  };

  const handleTriggerClick = () => {
    if (disabled) return;

    if (isOpen) {
      setIsOpen(false);
      return;
    }

    setOpenUp(resolveOpenDirection());
    setIsOpen(true);
  };

  const handleOptionClick = (optionId) => {
    if (disabled) return;

    if (optionId === value) {
      setIsOpen(false);
      return;
    }

    onChange?.(optionId);
    setIsOpen(false);
  };

  if (!isMobile) {
    return (
      <div
        className="community-date-range"
        role="group"
        aria-label={__("futures.community.range")}
      >
        {COMMUNITY_TIME_RANGE_OPTIONS.map((option) => {
          const isActive = value === option.id;

          return (
            <button
              key={option.id}
              type="button"
              className={`community-date-range__chip${
                isActive ? " community-date-range__chip--active" : ""
              }`}
              onClick={() => onChange?.(option.id)}
              disabled={disabled}
              aria-pressed={isActive}
            >
              {__(option.labelKey)}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`community-date-range community-date-range--mobile${
        isOpen ? " community-date-range--open" : ""
      }${openUp ? " community-date-range--up" : " community-date-range--down"}`}
    >
      {!isOpen ? (
        <button
          type="button"
          className="community-date-range__chip community-date-range__chip--active community-date-range__trigger"
          onClick={handleTriggerClick}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={false}
          aria-label={__("futures.community.range")}
        >
          {__(selectedOption.labelKey)}
        </button>
      ) : (
        <div
          className="community-date-range__menu"
          role="listbox"
          aria-label={__("futures.community.range")}
        >
          {COMMUNITY_TIME_RANGE_OPTIONS.map((option) => {
            const isActive = value === option.id;

            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={isActive}
                className={`community-date-range__chip${
                  isActive ? " community-date-range__chip--active" : ""
                }`}
                onClick={() => handleOptionClick(option.id)}
                disabled={disabled}
              >
                {__(option.labelKey)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

CommunityDateRange.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

export default CommunityDateRange;
